import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import MapView from "../components/MapView";
import PropertyList from "../components/PropertyList";
import PropertyDrawer from "../components/PropertyDrawer";
import PropertyFormDialog from "../components/PropertyFormDialog";
import AIAnalysisPanel from "../components/AIAnalysisPanel";
import AIPortfolioDashboard from "../components/AIPortfolioDashboard";
import QuickFieldEntry from "../components/QuickFieldEntry";
import ImportListingDialog from "../components/ImportListingDialog";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileDrawer, { MobileHamburgerButton } from "../components/MobileDrawer";
import { DASHBOARD } from "../constants/testIds";
import { toast } from "sonner";
import { readModalFromUrl, pushOpenModal, popCloseModal } from "../utils/modalHistory";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [view, setViewState] = useState(() => {
    // Initialize view from URL query (?view=map|list|ai-panel)
    const q = new URLSearchParams(window.location.search).get("view");
    return q === "list" || q === "ai-panel" ? q : "map";
  });
  // §3 Browser Back/Forward — keep view <-> URL in sync.
  const setView = useCallback((next) => {
    setViewState(next);
    const url = new URL(window.location.href);
    if (next === "map") url.searchParams.delete("view");
    else url.searchParams.set("view", next);
    // Preserve modal params so switching view while a modal is open doesn't
    // silently close the modal.
    window.history.pushState({ view: next }, "", url.toString());
  }, []);

  // Modal state derived from URL. See src/utils/modalHistory.js
  const [modalState, setModalState] = useState(() => readModalFromUrl());
  const modalId = modalState.id;
  const modalPid = modalState.pid;
  const isFormDialogOpen  = modalId === "property-new" || modalId === "property-edit";
  const isQuickEntryOpen  = modalId === "quick-entry";
  const isImportListingOpen = modalId === "import";
  const isPropertyDrawerOpen = modalId === "property-view";
  const isAIPanelOpen     = modalId === "ai-single";

  // React to back/forward: sync view + modal state from URL.
  useEffect(() => {
    const onPop = () => {
      const q = new URLSearchParams(window.location.search).get("view");
      const v = q === "list" || q === "ai-panel" ? q : "map";
      setViewState(v);
      setModalState(readModalFromUrl());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Whenever popstate opens property-view/edit modal via URL, ensure we have
  // the property object loaded (e.g. deep-link refresh).
  useEffect(() => {
    if ((modalId === "property-view" || modalId === "property-edit") && modalPid) {
      const p = properties.find(pp => pp.id === modalPid);
      if (p) {
        if (modalId === "property-edit") setEditingProperty(p);
        else setSelectedProperty(p);
      }
    }
    if (modalId === "property-new") setEditingProperty(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId, modalPid, properties.length]);
  const [isAIPanelOpenDeprecated_notused] = useState(false);  // legacy, replaced by URL-driven modalId
  const [selectedForAnalysis, setSelectedForAnalysis] = useState([]);
  const [importDraft, setImportDraft] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const fetchProperties = useCallback(async () => {
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const { data } = await axios.get(`${API_URL}/api/properties`, {
        params,
        withCredentials: true,
      });
      setProperties(data);
    } catch (error) {
      console.error("Error fetching properties:", error?.message || error);
      toast.error("Gayrimenkuller yüklenirken hata oluştu");
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (user) {
      fetchProperties();
    }
  }, [user, fetchProperties]);

  // §1 Cross-device sync: refetch when window regains focus or tab becomes
  // visible, and when the view switches. This ensures that entries created
  // on another device (e.g. mobile Hızlı Saha Kaydı) become visible on the
  // desktop as soon as the user comes back to the tab.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => fetchProperties();
    const onVis = () => { if (document.visibilityState === "visible") fetchProperties(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, fetchProperties]);

  // Refetch on view change so navigation to Harita/Liste/AI Panel always
  // shows the latest server-persisted records.
  useEffect(() => {
    if (user) fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // §8 Scroll reset: when the main view changes, scroll all scrollable
  // descendants of the main content container to top so headings/action
  // buttons of the newly-opened view are immediately visible.
  const mainScrollRef = React.useRef(null);
  useEffect(() => {
    if (!mainScrollRef.current) return;
    const scrollables = mainScrollRef.current.querySelectorAll(".overflow-y-auto");
    scrollables.forEach((el) => { el.scrollTop = 0; });
  }, [view]);

  // If arrived via /import?draft=<id> — load draft & open ImportListingDialog
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const draftId = params.get("draft");
    if (!draftId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/extension/drafts/${encodeURIComponent(draftId)}`,
          { withCredentials: true }
        );
        setImportDraft(data);
        // Clean the URL and open the import modal via the history helper so
        // Back closes it as expected.
        navigate("/dashboard", { replace: true });
        pushOpenModal("import");
        setModalState(readModalFromUrl());
      } catch (e) {
        toast.error(
          e.response?.status === 410
            ? "Aktarım süresi dolmuş. Lütfen eklentiden tekrar aktarın."
            : e.response?.data?.detail || "Aktarım verisi bulunamadı"
        );
        navigate("/dashboard", { replace: true });
      }
    })();
  }, [user, location.search, navigate]);

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
    pushOpenModal("property-view", { pid: property.id });
    setModalState(readModalFromUrl());
  };

  const handleAddProperty = () => {
    setEditingProperty(null);
    pushOpenModal("property-new");
    setModalState(readModalFromUrl());
    setMobileDrawerOpen(false);
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    pushOpenModal("property-edit", { pid: property.id });
    setModalState(readModalFromUrl());
  };

  const closeModal = () => {
    popCloseModal();
    // popstate listener will sync modalState.
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/properties/${propertyId}`, {
        withCredentials: true,
      });
      toast.success("Gayrimenkul silindi");
      fetchProperties();
      // Property deletion removes the underlying record; close the drawer
      // by replacing history rather than pushing, so Back doesn't re-open a
      // now-nonexistent record.
      const url = new URL(window.location.href);
      url.searchParams.delete("modal");
      url.searchParams.delete("pid");
      window.history.replaceState({}, "", url.toString());
      setModalState(readModalFromUrl());
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleFormSubmit = async () => {
    closeModal();
    setEditingProperty(null);
    fetchProperties();
  };

  const filteredProperties = (selectedCategory
    ? properties.filter((p) => p.category === selectedCategory)
    : properties
  ).filter((p) => (selectedStatus ? p.status === selectedStatus : true));

  const sidebar = (
    <Sidebar
      view={view}
      setView={(v) => { setView(v); setMobileDrawerOpen(false); }}
      selectedCategory={selectedCategory}
      setSelectedCategory={(c) => { setSelectedCategory(c); setMobileDrawerOpen(false); }}
      selectedStatus={selectedStatus}
      setSelectedStatus={setSelectedStatus}
      onAddProperty={handleAddProperty}
      onOpenAIPanel={() => { navigate("/portfolio-analysis"); setMobileDrawerOpen(false); }}
      onOpenQuickEntry={() => { pushOpenModal("quick-entry"); setModalState(readModalFromUrl()); setMobileDrawerOpen(false); }}
      onOpenImportListing={() => { pushOpenModal("import"); setModalState(readModalFromUrl()); setMobileDrawerOpen(false); }}
    />
  );

  return (
    <div
      data-testid={DASHBOARD.container}
      className="h-screen flex bg-[#F9FAFB] dark:bg-[#09090B] overflow-hidden"
    >
      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:h-screen">
        {sidebar}
      </div>

      {/* Mobile hamburger */}
      <MobileHamburgerButton onClick={() => setMobileDrawerOpen(true)} />
      <MobileDrawer open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)}>
        {sidebar}
      </MobileDrawer>

      <div ref={mainScrollRef} className="flex-1 flex overflow-hidden pb-[64px] lg:pb-0">
        {view === "map" ? (
          <>
            <div className="flex-1 relative">
              <MapView
                properties={filteredProperties}
                onPropertyClick={handlePropertyClick}
              />
            </div>
            <div className="hidden lg:block w-[400px] border-l border-[#E4E4E7] dark:border-[#27272A] overflow-y-auto">
              <PropertyList
                properties={filteredProperties}
                onPropertyClick={handlePropertyClick}
                selectedForAnalysis={selectedForAnalysis}
                setSelectedForAnalysis={setSelectedForAnalysis}
                onOpenAIPanel={() => { pushOpenModal("ai-single"); setModalState(readModalFromUrl()); }}
              />
            </div>
          </>
        ) : view === "ai-panel" ? (
          <div className="flex-1 overflow-y-auto">
            <AIPortfolioDashboard onPropertyClick={handlePropertyClick} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <PropertyList
              properties={filteredProperties}
              onPropertyClick={handlePropertyClick}
              selectedForAnalysis={selectedForAnalysis}
              setSelectedForAnalysis={setSelectedForAnalysis}
              onOpenAIPanel={() => { pushOpenModal("ai-single"); setModalState(readModalFromUrl()); }}
              gridView
            />
          </div>
        )}
      </div>

      <PropertyDrawer
        property={selectedProperty}
        open={isPropertyDrawerOpen}
        onClose={closeModal}
        onEdit={handleEditProperty}
        onDelete={handleDeleteProperty}
      />

      <PropertyFormDialog
        open={isFormDialogOpen}
        onClose={() => { closeModal(); setEditingProperty(null); }}
        property={editingProperty}
        onSubmit={handleFormSubmit}
      />

      <AIAnalysisPanel
        open={isAIPanelOpen}
        onClose={closeModal}
        selectedProperties={properties.filter((p) =>
          selectedForAnalysis.includes(p.id)
        )}
      />

      <QuickFieldEntry
        open={isQuickEntryOpen}
        onClose={closeModal}
        onSaved={fetchProperties}
      />

      <ImportListingDialog
        open={isImportListingOpen}
        onClose={() => { closeModal(); setImportDraft(null); }}
        onSaved={fetchProperties}
        initialDraft={importDraft}
      />

      {/* Mobile bottom nav */}
      <MobileBottomNav
        view={view}
        setView={setView}
        onOpenQuickEntry={() => { pushOpenModal("quick-entry"); setModalState(readModalFromUrl()); }}
      />
    </div>
  );
};

export default Dashboard;
